import { useEffect, useRef, useState, useCallback } from 'react';
import { getWsTicket } from '../utils/wsTicket';

/**
 * useChatSocket
 * --------------
 * Connecte au WebSocket /api/ws/chat/{conversationId} pour recevoir en temps réel :
 *   - new_message
 *   - user_typing
 *   - message_read
 *   - user_joined / user_left
 *   - connection_confirmed
 *
 * Si conversationId est null/undefined, n'établit pas de connexion.
 * Reconnexion automatique avec backoff exponentiel (max 30s).
 *
 * SEC-003 — Auth WS via ticket court-vie (cf. utils/wsTicket.js).
 *
 * Usage :
 *   const { connected, lastMessage } = useChatSocket(conv?.id, user?.id, user?.role);
 *   useEffect(() => { if (lastMessage?.type === 'new_message') ... }, [lastMessage]);
 */
export const useChatSocket = (conversationId, userId, userRole) => {
  const [connected, setConnected] = useState(false);
  const [lastMessage, setLastMessage] = useState(null);
  const wsRef = useRef(null);
  const reconnectAttempts = useRef(0);
  const reconnectTimer = useRef(null);
  const intentionalClose = useRef(false);

  const buildUrl = useCallback(async () => {
    const base = process.env.REACT_APP_BACKEND_URL || '';
    const wsBase = base.replace(/^http/, 'ws');
    const _opsSet = new Set(['operateur', 'ops', 'lead_ops']);
    const userType = ['client', 'expediteur'].includes(userRole) ? 'client'
      : _opsSet.has(userRole) ? 'operator'
      : userRole === 'admin' ? 'admin'
      : userRole || 'client';
    // SEC-003 — Récupère un ticket WS court-vie (60s) via /api/auth/ws-ticket
    const ticket = await getWsTicket();
    if (!ticket) return null;
    return `${wsBase}/api/ws/chat/${conversationId}?user_id=${encodeURIComponent(userId)}&user_type=${encodeURIComponent(userType)}&token=${encodeURIComponent(ticket)}`;
  }, [conversationId, userId, userRole]);

  const connect = useCallback(async () => {
    if (!conversationId || !userId) return;
    intentionalClose.current = false;

    try {
      const url = await buildUrl();
      if (!url) return; // pas authentifié, on n'ouvre rien
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        setConnected(true);
        reconnectAttempts.current = 0;
      };
      ws.onmessage = (evt) => {
        try {
          const msg = JSON.parse(evt.data);
          setLastMessage({ ...msg, _receivedAt: Date.now() });
          // Diffuser un event global pour les hooks qui suivent les conversations (badges, etc.)
          if (msg.type === 'new_message' || msg.type === 'conversation_updated') {
            try {
              window.dispatchEvent(new CustomEvent('baobab:conversation-updated', { detail: msg }));
            } catch (e) { /* ignore */ }
          }
        } catch (e) {
          // Ignore malformed
        }
      };
      ws.onerror = () => {
        // onclose fera la reconnexion
      };
      ws.onclose = () => {
        setConnected(false);
        if (intentionalClose.current) return;
        const attempt = ++reconnectAttempts.current;
        const delay = Math.min(1000 * 2 ** attempt, 30000);
        reconnectTimer.current = setTimeout(connect, delay);
      };
    } catch (e) {
      console.error('Chat socket error:', e);
    }
  }, [conversationId, userId, buildUrl]);

  useEffect(() => {
    connect();
    return () => {
      intentionalClose.current = true;
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      if (wsRef.current && wsRef.current.readyState <= 1) {
        try { wsRef.current.close(); } catch (e) { /* noop */ }
      }
      setConnected(false);
    };
  }, [connect]);

  // Send helper (typing, ping…)
  const send = useCallback((payload) => {
    if (wsRef.current && wsRef.current.readyState === 1) {
      wsRef.current.send(JSON.stringify(payload));
      return true;
    }
    return false;
  }, []);

  // Helper typing indicator (debounced)
  const typingTimer = useRef(null);
  const sendTyping = useCallback(() => {
    if (!wsRef.current || wsRef.current.readyState !== 1) return;
    wsRef.current.send(JSON.stringify({ type: 'typing', is_typing: true }));
    if (typingTimer.current) clearTimeout(typingTimer.current);
    typingTimer.current = setTimeout(() => {
      try {
        if (wsRef.current && wsRef.current.readyState === 1) {
          wsRef.current.send(JSON.stringify({ type: 'typing', is_typing: false }));
        }
      } catch (e) { /* noop */ }
    }, 2500);
  }, []);

  return { connected, lastMessage, send, sendTyping };
};

export default useChatSocket;
